import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import {
  Database,
  Search,
  RefreshCw,
  Trash2,
  Filter,
  X,
} from "lucide-react";
import { toast } from "sonner";
import EmbeddingsDataTable from "@/components/data-tables/embeddings/EmbeddingsDataTable";

interface Point {
  id: string | number;
  payload: Record<string, any>;
}

interface CollectionInfo {
  name: string;
  pointsCount: number;
  vectorSize: number;
  distance: string;
  status: string;
}

const EmbeddingsViewer = () => {
  const { t } = useTranslation();
  const [selectedCollection, setSelectedCollection] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterField, setFilterField] = useState<string>("");
  const [filterValue, setFilterValue] = useState<string>("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    {}
  );
  const [selectedPoint, setSelectedPoint] = useState<Point | null>(null);
  const [pointToDelete, setPointToDelete] = useState<string | null>(null);
  const [collectionToDelete, setCollectionToDelete] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCollectionDialogOpen, setDeleteCollectionDialogOpen] = useState(false);

  // Fetch collections
  const {
    data: collectionsData,
    isLoading: collectionsLoading,
    refetch: refetchCollections,
  } = trpc.embeddings.listCollections.useQuery();

  // Fetch collection info
  const {
    data: collectionInfo,
    isLoading: collectionInfoLoading,
    refetch: refetchCollectionInfo,
  } = trpc.embeddings.getCollectionInfo.useQuery(
    { collectionName: selectedCollection },
    { enabled: !!selectedCollection }
  ) as { 
    data: CollectionInfo | undefined; 
    isLoading: boolean; 
    refetch: () => void;
  };

  // Fetch points
  const {
    data: pointsData,
    isLoading: pointsLoading,
    refetch: refetchPoints,
  } = trpc.embeddings.listPoints.useQuery(
    {
      collectionName: selectedCollection,
      limit: 100,
    },
    { enabled: !!selectedCollection && Object.keys(activeFilters).length === 0 }
  );

  // Fetch filtered points
  const {
    data: filteredPointsData,
    isLoading: filteredPointsLoading,
    refetch: refetchFilteredPoints,
  } = trpc.embeddings.searchPoints.useQuery(
    {
      collectionName: selectedCollection,
      filter: activeFilters,
      limit: 100,
    },
    { enabled: !!selectedCollection && Object.keys(activeFilters).length > 0 }
  );

  // Fetch metadata fields
  const { data: metadataFields } = trpc.embeddings.getMetadataFields.useQuery(
    { collectionName: selectedCollection },
    { enabled: !!selectedCollection }
  );

  // Delete point mutation
  const deletePointMutation = trpc.embeddings.deletePoint.useMutation({
    onSuccess: () => {
      toast.success(t("embeddings.point_deleted"));
      refetchPoints();
      refetchFilteredPoints();
      refetchCollectionInfo();
      setDeleteDialogOpen(false);
      setPointToDelete(null);
    },
    onError: (error) => {
      toast.error(t("embeddings.delete_error", { error: error.message }));
    },
  });

  // Delete collection mutation
  const deleteCollectionMutation = trpc.embeddings.deleteCollection.useMutation({
    onSuccess: () => {
      toast.success(t("embeddings.collection_deleted"));
      refetchCollections();
      setDeleteCollectionDialogOpen(false);
      setCollectionToDelete(null);
      // Reset selected collection if it was deleted
      if (selectedCollection === collectionToDelete) {
        setSelectedCollection("");
      }
    },
    onError: (error) => {
      toast.error(t("embeddings.collection_delete_error", { error: error.message }));
    },
  });

  // Set first collection as default
  useEffect(() => {
    if (
      collectionsData?.collections &&
      collectionsData.collections.length > 0 &&
      !selectedCollection
    ) {
      setSelectedCollection(collectionsData.collections[0].name);
    }
  }, [collectionsData, selectedCollection]);

  const currentPoints = useMemo(() => {
    if (Object.keys(activeFilters).length > 0) {
      return filteredPointsData?.points || [];
    }
    return pointsData?.points || [];
  }, [activeFilters, filteredPointsData, pointsData]);

  const currentLoading = useMemo(() => {
    if (Object.keys(activeFilters).length > 0) {
      return filteredPointsLoading;
    }
    return pointsLoading;
  }, [activeFilters, filteredPointsLoading, pointsLoading]);

  // Filter points by search query
  const filteredPoints = useMemo(() => {
    if (!searchQuery) return currentPoints;

    const query = searchQuery.toLowerCase();
    return currentPoints.filter((point) => {
      const pointStr = JSON.stringify(point).toLowerCase();
      return pointStr.includes(query);
    });
  }, [currentPoints, searchQuery]);

  const handleAddFilter = () => {
    if (filterField && filterValue) {
      setActiveFilters((prev) => ({ ...prev, [filterField]: filterValue }));
      setFilterField("");
      setFilterValue("");
    }
  };

  const handleRemoveFilter = (field: string) => {
    setActiveFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const handleViewDetails = (point: Point) => {
    setSelectedPoint(point);
    setDetailDialogOpen(true);
  };

  const handleDeleteClick = (pointId: string) => {
    setPointToDelete(pointId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCollectionClick = (collectionName: string) => {
    setCollectionToDelete(collectionName);
    setDeleteCollectionDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (pointToDelete && selectedCollection) {
      deletePointMutation.mutate({
        collectionName: selectedCollection,
        pointId: pointToDelete,
      });
    }
  };

  const handleDeleteCollectionConfirm = () => {
    if (collectionToDelete) {
      deleteCollectionMutation.mutate({
        collectionName: collectionToDelete,
      });
    }
  };

  const handleRefresh = () => {
    refetchCollections();
    refetchCollectionInfo();
    refetchPoints();
    refetchFilteredPoints();
    toast.success(t("embeddings.refreshed"));
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6" />
            {t("embeddings.title", "Embeddings Viewer")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t(
              "embeddings.description",
              "View and manage vector embeddings stored in Qdrant"
            )}
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t("embeddings.refresh", "Refresh")}
        </Button>
      </div>

      {/* Collections Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("embeddings.collections", "Collections")}
          </CardTitle>
          <CardDescription>
            {t(
              "embeddings.collections_description",
              "Select a collection to view its embeddings"
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {collectionsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {collectionsData?.collections.map((collection) => (
                <Card
                  key={collection.name}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedCollection === collection.name
                      ? "border-primary border-2"
                      : ""
                  }`}
                  onClick={() => setSelectedCollection(collection.name)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{collection.name}</span>
                      {selectedCollection === collection.name && (
                        <Badge variant="default" className="ml-2">
                          {t("embeddings.selected", "Selected")}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Collection Details & Points */}
      {selectedCollection && (
        <>
          {/* Collection Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">
                    {t("embeddings.collection_info", "Collection Information")}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {selectedCollection}
                  </CardDescription>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteCollectionClick(selectedCollection)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("embeddings.delete_collection", "Delete Collection")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {collectionInfoLoading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-16" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("embeddings.points_count", "Points")}
                    </p>
                    <p className="text-2xl font-bold">
                      {collectionInfo?.pointsCount || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("embeddings.vector_size", "Vector Size")}
                    </p>
                    <p className="text-2xl font-bold">
                      {collectionInfo?.vectorSize || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("embeddings.distance", "Distance Metric")}
                    </p>
                    <p className="text-xl font-bold">
                      {collectionInfo?.distance || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("embeddings.status", "Status")}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <div
                        className={`h-3 w-3 rounded-full ${
                          collectionInfo?.status === "green"
                            ? "bg-green-500"
                            : collectionInfo?.status === "yellow"
                            ? "bg-yellow-500"
                            : collectionInfo?.status === "red"
                            ? "bg-red-500"
                            : "bg-gray-500"
                        } ${
                          collectionInfo?.status === "green"
                            ? "shadow-green-500/50"
                            : ""
                        } shadow-lg`}
                      />
                      <span className="text-lg font-semibold capitalize">
                        {collectionInfo?.status || "unknown"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Search and Filter */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="h-4 w-4" />
                {t("embeddings.search_filter", "Search & Filter")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t(
                      "embeddings.search_placeholder",
                      "Search in metadata..."
                    )}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(activeFilters).map(([field, value]) => (
                  <Badge key={field} variant="secondary" className="gap-1">
                    {field}: {value}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => handleRemoveFilter(field)}
                    />
                  </Badge>
                ))}
              </div>

              {/* Add Filter */}
              <div className="flex gap-2">
                <Select value={filterField} onValueChange={setFilterField}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue
                      placeholder={t(
                        "embeddings.select_field",
                        "Select field"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {metadataFields &&
                      Object.keys(metadataFields.fields).map((field) => (
                        <SelectItem key={field} value={field}>
                          {field}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder={t("embeddings.filter_value", "Filter value")}
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={handleAddFilter}
                  disabled={!filterField || !filterValue}
                >
                  {t("embeddings.add_filter", "Add Filter")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Points Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("embeddings.points", "Embeddings")}
              </CardTitle>
              <CardDescription>
                {t("embeddings.showing", "Showing")} {filteredPoints.length}{" "}
                {t("embeddings.of", "of")} {currentPoints.length}{" "}
                {t("embeddings.points", "points")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {currentLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : filteredPoints.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {t("embeddings.no_points", "No embeddings found")}
                </div>
              ) : (
                <EmbeddingsDataTable
                  data={filteredPoints}
                  actions={{
                    onViewDetails: handleViewDetails,
                    onDelete: handleDeleteClick,
                    onRefresh: handleRefresh,
                  }}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("embeddings.point_details", "Point Details")}
            </DialogTitle>
            <DialogDescription>
              {t("embeddings.point_id", "Point ID")}: {selectedPoint?.id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">
                {t("embeddings.metadata", "Metadata")}
              </h3>
              <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs">
                {JSON.stringify(selectedPoint?.payload, null, 2)}
              </pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("embeddings.delete_title", "Delete Point")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "embeddings.delete_description",
                "Are you sure you want to delete this embedding? This action cannot be undone."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("embeddings.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t("embeddings.delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Collection Confirmation Dialog */}
      <AlertDialog open={deleteCollectionDialogOpen} onOpenChange={setDeleteCollectionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("embeddings.delete_collection_title", "Delete Collection")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "embeddings.delete_collection_description",
                "Are you sure you want to delete the collection '{{name}}'? This will permanently delete all embeddings in this collection. This action cannot be undone.",
                { name: collectionToDelete }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("embeddings.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCollectionConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t("embeddings.delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmbeddingsViewer;
